import * as THREE from 'three';
import { Viewer } from '../viewer/viewer';

export class DebugPointVisualizer {
  private viewer: Viewer;
  private debugSpheres: THREE.Mesh[] = [];
  private debugLines: THREE.Line[] = [];

  constructor(viewer: Viewer) {
    this.viewer = viewer;
  }

  /**
   * Clear all debug spheres and lines from the debug scene
   */
  clear() {
    const debugScene = this.viewer.scene.sceneDebug;
    for (const sphere of this.debugSpheres) {
      debugScene.remove(sphere);
    }
    this.debugSpheres = [];

    for (const line of this.debugLines) {
      debugScene.remove(line);
    }
    this.debugLines = [];
  }

  /**
   * Create debug spheres for points with weights and colors
   */
  visualizePoints(points: THREE.Vector3[], weights: number[], maxWeight: number, size = 0.1, opacity = 0.8) {
    const geometry = new THREE.SphereGeometry(size, 8, 6);

    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      const weight = weights[i] || 1.0;
      const normalizedWeight = Math.min(weight / maxWeight, 1.0);

      // Create color based on weight: Blue (low) to Red (high)
      const color = new THREE.Color();
      color.setRGB(normalizedWeight, 0, 1.0 - normalizedWeight);

      const material = new THREE.MeshBasicMaterial({
        color: color,
        transparent: true,
        opacity: opacity,
        depthTest: false, // Render on top of everything
        depthWrite: false,
      });

      const sphere = new THREE.Mesh(geometry, material);
      sphere.position.copy(point);
      sphere.renderOrder = 999; // Render after everything else

      // Add to debug scene so it renders on top of everything
      this.viewer.scene.sceneDebug.add(sphere);
      this.debugSpheres.push(sphere);
    }
  }

  /**
   * Get current debug spheres (for permanent storage)
   */
  getCurrentSpheres(): THREE.Mesh[] {
    return [...this.debugSpheres];
  }

  /**
   * Remove spheres from tracking but keep in scene (for permanent storage)
   */
  releaseCurrentSpheres(): THREE.Mesh[] {
    const spheres = [...this.debugSpheres];
    this.debugSpheres = [];
    return spheres;
  }

  /**
   * Visualize a plane normal as a line
   */
  visualizePlaneNormal(
    origin: THREE.Vector3,
    normal: THREE.Vector3,
    length = 1.0,
    color: THREE.Color = new THREE.Color(0xff00ff), // Magenta by default
  ) {
    // Create line geometry from origin to origin + normal * length
    const geometry = new THREE.BufferGeometry();
    const points = [origin, origin.clone().add(normal.clone().multiplyScalar(length))];
    geometry.setFromPoints(points);

    // Create line material
    const material = new THREE.LineBasicMaterial({
      color: color,
      linewidth: 3,
      depthTest: false, // Render on top of everything
      depthWrite: false,
    });

    // Create and add line to debug scene so it renders on top
    const line = new THREE.Line(geometry, material);
    line.renderOrder = 999; // Render after everything else
    this.viewer.scene.sceneDebug.add(line);
    this.debugLines.push(line);
  }

  /**
   * Visualize points from different nodes with different colors
   * @param pointsByNode Array of {nodeName, points} objects
   * @param size Size of debug spheres
   * @param opacity Opacity of debug spheres
   */
  visualizePointsByNode(pointsByNode: { nodeName: string; points: THREE.Vector3[] }[], size = 0.01, opacity = 0.8) {
    const geometry = new THREE.SphereGeometry(size, 8, 6);

    // Generate distinct colors for each node
    const nodeColors: THREE.Color[] = [];
    const hueStep = 360 / Math.max(pointsByNode.length, 1);

    for (let i = 0; i < pointsByNode.length; i++) {
      const hue = (i * hueStep) / 360;
      const color = new THREE.Color();
      color.setHSL(hue, 1.0, 0.5);
      nodeColors.push(color);
    }

    // Create spheres for each node's points
    pointsByNode.forEach((nodeData, nodeIndex) => {
      const color = nodeColors[nodeIndex];

      console.log(`Node ${nodeData.nodeName}: ${nodeData.points.length} points, color: HSL(${(nodeIndex * hueStep).toFixed(0)}°, 100%, 50%)`);

      const material = new THREE.MeshBasicMaterial({
        color: color,
        transparent: true,
        opacity: opacity,
        depthTest: false, // Render on top of everything
        depthWrite: false,
      });

      nodeData.points.forEach((point) => {
        const sphere = new THREE.Mesh(geometry, material);
        sphere.position.copy(point);
        sphere.renderOrder = 999; // Render after everything else
        // Add to debug scene so it renders on top of everything
        this.viewer.scene.sceneDebug.add(sphere);
        this.debugSpheres.push(sphere);
      });
    });

    console.log(`Total nodes visualized: ${pointsByNode.length}, Total points: ${pointsByNode.reduce((sum, node) => sum + node.points.length, 0)}`);
  }
}
